import { ElementRef, FC, useEffect, useRef, useState } from "react";
import PaginateCard from "../PaginateCard";

import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import Loader from "@/components/atoms/Loader";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { PaginateCardProps } from "@/interfaces/Card";
import { IFilters } from "@/interfaces/Fetch";
import IFile from "@/interfaces/File";
import {
  useDeleteCompanyFileMutation,
  useGetCompanyFilesQuery,
} from "@/services/companies/files";
import { useDownloadFileMutation } from "@/services/files";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { faDownload, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import AreYouSurToContinueModal from "../../modals/AreYouSurToContinueModal";

const CompanyFilesCard: FC<PaginateCardProps & { id: string } & IFilters> = ({
  onGetTotal,
  id,
  ...props
}) => {
  const user = useSelector(selectCurrentUser);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);

  const [filters, setFilters] = useState<IFilters>({
    pageIndex: 0,
    pageSize: 10,
    ...props,
  });

  const [deleteFileAsync] = useDeleteCompanyFileMutation();
  const [downloadFileAsync] = useDownloadFileMutation();
  const { data, isLoading, isError, error } = useGetCompanyFilesQuery([
    id,
    filters,
  ]);

  useEffect(() => {
    data?.meta && onGetTotal?.(data.meta.total);
  }, [data?.meta]);

  const onDelete = (fileId: string) => {
    toast.promise(deleteFileAsync([id, fileId]).unwrap(), {
      pending: "Suppression du fichier...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return "Fichier supprimé avec succès";
        },
      },
      error: "Erreur lors de la suppression du fichier",
    });
  };

  const onDownload = (file: IFile) => {
    toast.promise(downloadFileAsync(file).unwrap(), {
      pending: "Téléchargement du fichier...",
      success: "Fichier téléchargé avec succès",
      error: "Erreur lors du téléchargement du fichier",
    });
  };

  if (isLoading) {
    return <Loader />;
  } else if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <PaginateCard meta={data?.meta} filters={filters} setFilters={setFilters}>
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé le fichier qui a pour id : {{id}} ?"
      />
      {data?.data.map((file) => (
        <Flex
          key={file.id}
          alignItems="center"
          className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm hover-actions-trigger"
        >
          <div className="file-thumbnail">
            <embed
              src={file.link}
              className={"h-100 w-100 fit-cover rounded-2"}
            />
          </div>
          <div className="ms-3 flex-shrink-1 flex-grow-1">
            <h6 className="mb-1">
              <Link
                className="stretched-link text-900 fw-semi-bold"
                to={file.link}
                target="_blank"
              >
                {file.label ?? file.name}
              </Link>
            </h6>
            <div className="fs--1">
              <span className="fw-semi-bold">{file.author.fullname}</span>
              <span className="fw-medium text-600 ms-2">
                {DateTime.fromISO(file.createdAt).toFormat("dd/MM/yyyy HH:mm")}
              </span>
            </div>
            <div className="hover-actions end-0 top-50 p-x1 translate-middle-y">
              <OverlayTrigger
                overlay={<Tooltip id="nextTooltip">Télécharger</Tooltip>}
              >
                <Button
                  variant="success"
                  size="sm"
                  className="border-300 me-1 text-600"
                  onClick={() => onDownload(file)}
                >
                  <FontAwesomeIcon icon={faDownload} color="white" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                overlay={<Tooltip id="nextTooltip">Voir</Tooltip>}
              >
                <Link
                  className="btn btn-sm btn-info border-300 me-1 text-600"
                  to={file.link}
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faEye} color="white" />
                </Link>
              </OverlayTrigger>
              {checkIfUserHasRoles(user, ["ADMIN", "CSM", "AE"]) && (
                <OverlayTrigger
                  overlay={<Tooltip id="nextTooltip">Supprimer</Tooltip>}
                >
                  <Button
                    variant="danger"
                    size="sm"
                    className="border-300 text-600"
                    onClick={() =>
                      DeleteModalRef.current?.showOrHiddenModal(file.id)
                    }
                  >
                    <FontAwesomeIcon icon={faTrash} color="white" />
                  </Button>
                </OverlayTrigger>
              )}
            </div>
          </div>
        </Flex>
      ))}
    </PaginateCard>
  );
};

export default CompanyFilesCard;
